import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productsService, warehousesService, suppliesService, resourcesService, productLinesService, movementTypesService } from '../../services/productionService';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { MaskedInput } from '../../components/ui/masked-input';
import { Autocomplete } from '../../components/ui/autocomplete';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import TableComponent from '../../components/TableComponent';
import BOMEditor from '../../components/production/BOMEditor';
import Drawer from '../../components/Drawer';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { formatDateBR, formatQuantity, formatSignedQuantity, localDateISO, parseQuantityInput } from '../../lib/utils';

/** @type {Record<string, string>} tipos alinhados à API (App\Support\StockMovementType) */
const STOCK_TYPE_LABELS = {
    production_report: 'Produção',
    inventory_in: 'Entrada inventário',
    order_out: 'Saída de pedido',
    invoice_out: 'Saída de NF',
    inventory_out: 'Saída de inventário',
    scrap: 'Refugo',
    second_line: '2ª linha',
    adjustment: 'Ajuste (legado)',
};

const INCREMENT_TYPES = ['production_report', 'inventory_in'];

function ProductForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;
    usePageTitle(isEditing ? 'Editar Produto' : 'Novo Produto');
    const { toast, toastApiError } = useToast();

    const [product, setProduct] = useState(null);
    const [warehouses, setWarehouses] = useState([]);
    const [productLines, setProductLines] = useState([]);
    const [supplies, setSupplies] = useState([]);
    const [resources, setResources] = useState([]);
    // Single source of truth for all tabs (matches API shape)
    const [productDraft, setProductDraft] = useState({
        code: '',
        description: '',
        unit: '',
        product_line_id: null,
        status: 'active',
        min_stock: 0,
        max_stock: 1000,
        reorder_stock: 1000,
        sale_price: '',
        default_warehouse_id: null,
        supplies: [],
        resources: [],
    });
    const [movementTypesCatalog, setMovementTypesCatalog] = useState([]);
    const [defaultStockMovementTypeId, setDefaultStockMovementTypeId] = useState(null);
    const [stockMovements, setStockMovements] = useState([]);
    const [stockMovementsLoading, setStockMovementsLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [formLoading, setFormLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [adjustmentDrawerOpen, setAdjustmentDrawerOpen] = useState(false);
    const [adjustmentLoading, setAdjustmentLoading] = useState(false);
    const [adjustmentFormData, setAdjustmentFormData] = useState({
        warehouse_id: '',
        quantity: '',
        movement_type_id: '',
        movement_date: localDateISO(),
        document: '',
        empenho: '',
        notes: '',
    });

    const bomEditorRef = useRef(null);

    useEffect(() => {
        loadWarehouses();
        loadProductLines();
        loadBOMData(); // Carrega supplies e resources uma vez
        if (isEditing) {
            loadProduct();
            loadStockMovements();
        } else {
            setLoading(false);
        }
    }, [id, isEditing]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await movementTypesService.list({ per_page: 500 });
                const rows = response.data?.data || response.data || [];
                const arr = Array.isArray(rows) ? rows : [];
                if (!cancelled) {
                    setMovementTypesCatalog(arr);
                    const ie = arr.find((t) => String(t.code || '').toUpperCase() === 'IE');
                    setDefaultStockMovementTypeId(ie?.id ?? arr.find((t) => t.direction === 'in')?.id ?? null);
                }
            } catch {
                /* ignore - drawer exige seleção manual */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const loadWarehouses = async () => {
        try {
            const response = await warehousesService.list();
            setWarehouses(response.data.data || response.data);
        } catch (error) {
            console.error('Error loading warehouses:', error);
        }
    };

    const loadProductLines = async () => {
        try {
            const response = await productLinesService.list({ per_page: 1000 });
            setProductLines(response.data.data || response.data);
        } catch (error) {
            console.error('Error loading product lines:', error);
        }
    };

    const loadBOMData = async () => {
        try {
            const [suppliesRes, resourcesRes] = await Promise.all([
                suppliesService.list({ per_page: 1000 }),
                resourcesService.list({ per_page: 1000 }),
            ]);
            setSupplies(suppliesRes.data.data || suppliesRes.data);
            setResources(resourcesRes.data.data || resourcesRes.data);
        } catch (error) {
            console.error('Error loading BOM data:', error);
        }
    };

    const loadProduct = async () => {
        setLoading(true);
        try {
            const response = await productsService.get(id);
            const data = response.data.data || response.data;
            setProduct(data);
            setProductDraft({
                code: data.code || '',
                description: data.description || '',
                unit: data.unit || '',
                product_line_id: data.product_line_id != null ? Number(data.product_line_id) : null,
                status: data.status || 'active',
                min_stock: data.min_stock != null && data.min_stock !== '' ? Number(data.min_stock) : 0,
                max_stock: data.max_stock != null && data.max_stock !== '' ? Number(data.max_stock) : 1000,
                reorder_stock: data.reorder_stock != null && data.reorder_stock !== '' ? Number(data.reorder_stock) : 0,
                sale_price: data.sale_price != null && data.sale_price !== '' ? String(data.sale_price) : '',
                default_warehouse_id: data.default_warehouse_id != null ? Number(data.default_warehouse_id) : null,
                supplies: Array.isArray(data.supplies) ? data.supplies : [],
                resources: Array.isArray(data.resources) ? data.resources : [],
            });
        } catch (error) {
            console.error('Error loading product:', error);
            toastApiError(error, { title: 'Erro ao carregar', fallback: 'Erro ao carregar produto' });
            navigate('/production/products');
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (e) => {
        setProductDraft((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleProductLineSelect = (value) => {
        setProductDraft((prev) => ({
            ...prev,
            product_line_id: value != null ? Number(value) : null,
        }));
    };

    const handleSaveProduct = async () => {
        setFormLoading(true);

        try {
            const payload = {
                code: productDraft.code,
                description: productDraft.description,
                unit: productDraft.unit,
                product_line_id: productDraft.product_line_id != null && productDraft.product_line_id !== ''
                    ? parseInt(String(productDraft.product_line_id), 10)
                    : null,
                status: productDraft.status,
                min_stock: parseFloat(String(productDraft.min_stock ?? 0)) || 0,
                max_stock: parseFloat(String(productDraft.max_stock ?? 0)) || 0,
                reorder_stock: parseFloat(String(productDraft.reorder_stock ?? 0)) || 0,
                sale_price: productDraft.sale_price !== '' && productDraft.sale_price != null
                    ? parseFloat(String(productDraft.sale_price))
                    : null,
                default_warehouse_id:
                    productDraft.default_warehouse_id != null && productDraft.default_warehouse_id !== ''
                        ? parseInt(String(productDraft.default_warehouse_id), 10)
                        : null,
                supplies: (productDraft.supplies || []).map((s, idx) => ({
                    supply_id: s.supply_id,
                    quantity_per_unit: parseQuantityInput(s.quantity_per_unit),
                    sort_order: s.sort_order ?? (idx + 1),
                })),
                resources: (productDraft.resources || []).map((r, idx) => ({
                    resource_id: r.resource_id,
                    quantity_per_unit: parseQuantityInput(r.quantity_per_unit),
                    sort_order: r.sort_order ?? (idx + 1),
                })),
            };

            let savedProduct;
            if (isEditing) {
                const response = await productsService.update(id, payload);
                savedProduct = response.data.data || response.data;
            } else {
                const response = await productsService.create(payload);
                savedProduct = response.data.data || response.data;
            }

            // Se for novo produto, redireciona para edição para mostrar o BOM
            if (!isEditing && savedProduct.id) {
                toast({
                    title: 'Produto salvo',
                    description: 'Produto criado com sucesso',
                    variant: 'success',
                });
                navigate(`/production/products/${savedProduct.id}/edit`);
            } else {
                toast({
                    title: 'Produto salvo',
                    description: 'Alterações salvas com sucesso',
                    variant: 'success',
                });
                setProduct(savedProduct);
                setProductDraft((prev) => ({
                    ...prev,
                    code: savedProduct.code ?? prev.code,
                    description: savedProduct.description ?? prev.description,
                    unit: savedProduct.unit ?? prev.unit,
                    product_line_id:
                        savedProduct.product_line_id != null && savedProduct.product_line_id !== ''
                            ? Number(savedProduct.product_line_id)
                            : null,
                    status: savedProduct.status ?? prev.status,
                    min_stock: savedProduct.min_stock != null ? Number(savedProduct.min_stock) : prev.min_stock,
                    max_stock: savedProduct.max_stock != null ? Number(savedProduct.max_stock) : prev.max_stock,
                    reorder_stock: savedProduct.reorder_stock != null ? Number(savedProduct.reorder_stock) : prev.reorder_stock,
                    sale_price: savedProduct.sale_price != null ? String(savedProduct.sale_price) : prev.sale_price,
                    default_warehouse_id:
                        savedProduct.default_warehouse_id != null
                            ? Number(savedProduct.default_warehouse_id)
                            : null,
                    supplies: Array.isArray(savedProduct.supplies) ? savedProduct.supplies : (prev.supplies || []),
                    resources: Array.isArray(savedProduct.resources) ? savedProduct.resources : (prev.resources || []),
                }));
            }
        } catch (error) {
            console.error('Error saving product:', error);
            toastApiError(error, { title: 'Erro ao salvar', fallback: 'Erro ao salvar produto' });
        } finally {
            setFormLoading(false);
        }
    };

    const loadStockMovements = async () => {
        if (!id) return;

        setStockMovementsLoading(true);
        try {
            const response = await productsService.getStockMovements(id);
            const data = response.data.data || response.data;
            setStockMovements(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error loading stock movements:', error);
            setStockMovements([]);
        } finally {
            setStockMovementsLoading(false);
        }
    };

    const resolveDefaultWarehouseId = () => {
        if (productDraft.default_warehouse_id != null && productDraft.default_warehouse_id !== '') {
            return productDraft.default_warehouse_id;
        }
        if (product?.default_warehouse_id != null) {
            return product.default_warehouse_id;
        }
        return null;
    };

    const buildFreshAdjustmentForm = () => {
        const defaultWarehouseId = resolveDefaultWarehouseId();
        return {
            warehouse_id: defaultWarehouseId != null ? String(defaultWarehouseId) : '',
            quantity: '',
            movement_type_id: defaultStockMovementTypeId != null ? String(defaultStockMovementTypeId) : '',
            movement_date: localDateISO(),
            document: '',
            empenho: '',
            notes: '',
        };
    };

    const openStockAdjustmentDrawer = () => {
        setAdjustmentFormData(buildFreshAdjustmentForm());
        setAdjustmentDrawerOpen(true);
    };

    const handleAdjustmentSubmit = async (e) => {
        e.preventDefault();
        const qty = parseQuantityInput(adjustmentFormData.quantity);
        if (!Number.isFinite(qty) || qty <= 0) {
            toast({
                title: 'Quantidade inválida',
                description: 'Informe uma quantidade maior que zero.',
                variant: 'error',
            });
            return;
        }
        const mid = adjustmentFormData.movement_type_id
            ? parseInt(adjustmentFormData.movement_type_id, 10)
            : null;
        if (!mid || Number.isNaN(mid)) {
            toast({
                title: 'Tipo obrigatório',
                description: 'Selecione um tipo de movimentação cadastrado.',
                variant: 'error',
            });
            return;
        }
        setAdjustmentLoading(true);
        try {
            await productsService.adjustInventory(id, {
                warehouse_id: parseInt(adjustmentFormData.warehouse_id, 10),
                movement_type_id: mid,
                quantity: qty,
                movement_date: adjustmentFormData.movement_date,
                document: adjustmentFormData.document?.trim() || null,
                empenho: adjustmentFormData.empenho?.trim() || null,
                notes: adjustmentFormData.notes?.trim() || null,
            });
            setAdjustmentFormData(buildFreshAdjustmentForm());
            setAdjustmentDrawerOpen(false);
            loadStockMovements();
            toast({
                title: 'Ajuste aplicado',
                description: 'A movimentação foi registrada com sucesso',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error adjusting inventory:', error);
            toastApiError(error, { title: 'Erro no ajuste', fallback: 'Erro ao realizar ajuste de inventário' });
        } finally {
            setAdjustmentLoading(false);
        }
    };

    const handleAdjustmentFormChange = (e) => {
        setAdjustmentFormData({
            ...adjustmentFormData,
            [e.target.name]: e.target.value,
        });
    };

    const getMovementTypeDisplay = (row) => {
        const label = row.type_label || STOCK_TYPE_LABELS[row.type] || row.type || '-';
        const dir = row.movement_type?.direction;
        const inc =
            dir === 'in'
                ? true
                : dir === 'out'
                  ? false
                  : INCREMENT_TYPES.includes(row.type);
        const cls =
            inc ? 'bg-emerald-100 text-emerald-900'
                : row.type === 'adjustment'
                  ? 'bg-amber-100 text-amber-900'
                  : 'bg-rose-100 text-rose-900';
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>{label}</span>
        );
    };


    return (
        <AppLayout>
            <div className="space-y-6">
                {/* Header */}
                <PageHeader
                    title={isEditing ? `Editar Produto - ${product?.code ?? ''}` : 'Novo Produto'}
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Produção', path: '/production/products' },
                        { label: 'Produtos', path: '/production/products' },
                    ]}
                    actions={[
                        {
                            label: 'Voltar',
                            icon: 'back',
                            variant: 'outline',
                            as: Link,
                            to: '/production/products',
                        },
                        {
                            label: formLoading ? 'Salvando...' : 'Salvar',
                            icon: 'save',
                            onClick: handleSaveProduct,
                            disabled: formLoading || loading,
                        },
                    ]}
                />

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="general" activeValue={activeTab} onValueChange={setActiveTab}>
                            Geral
                        </TabsTrigger>
                        {isEditing && (
                            <TabsTrigger value="bom" activeValue={activeTab} onValueChange={setActiveTab}>
                                BOM
                            </TabsTrigger>
                        )}
                        {isEditing && (
                            <TabsTrigger value="stock" activeValue={activeTab} onValueChange={setActiveTab}>
                                Movimentação de Estoque
                            </TabsTrigger>
                        )}
                    </TabsList>

                    {/* Tab: Geral */}
                    <TabsContent value="general" activeValue={activeTab} className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Informações do Produto</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Código *
                                            </label>
                                            <Input
                                                name="code"
                                                value={productDraft.code}
                                                onChange={handleFormChange}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Unidade *
                                            </label>
                                            <Input
                                                name="unit"
                                                value={productDraft.unit}
                                                onChange={handleFormChange}
                                                placeholder="un, kg, L, etc"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Descrição *
                                        </label>
                                        <Input
                                            name="description"
                                            value={productDraft.description}
                                            onChange={handleFormChange}
                                            required
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Status *
                                            </label>
                                            <select
                                                name="status"
                                                value={productDraft.status}
                                                onChange={handleFormChange}
                                                className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                required
                                            >
                                                <option value="active">Ativo</option>
                                                <option value="inactive">Inativo</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Linha
                                            </label>
                                            <Autocomplete
                                                options={productLines}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id}
                                                value={productDraft.product_line_id != null ? productDraft.product_line_id : null}
                                                onChange={handleProductLineSelect}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-sm font-medium text-gray-700 mb-2">Limites de estoque (unidades)</p>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm text-gray-600 mb-1">
                                                    Estoque mínimo *
                                                </label>
                                                <Input
                                                    name="min_stock"
                                                    type="number"
                                                    min="0"
                                                    step="0.001"
                                                    value={String(productDraft.min_stock ?? 0)}
                                                    onChange={handleFormChange}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-600 mb-1">
                                                    Estoque máximo *
                                                </label>
                                                <Input
                                                    name="max_stock"
                                                    type="number"
                                                    min="0"
                                                    step="0.001"
                                                    value={String(productDraft.max_stock ?? 0)}
                                                    onChange={handleFormChange}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-gray-600 mb-1">
                                                    Reposição *
                                                </label>
                                                <Input
                                                    name="reorder_stock"
                                                    type="number"
                                                    min="0"
                                                    step="0.001"
                                                    value={String(productDraft.reorder_stock ?? 0)}
                                                    onChange={handleFormChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                            O saldo global do produto (soma das movimentações) é comparado aos limites. Reposição define a quantidade sugerida de recomposição.
                                        </p>
                                    </div>

                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Preço de venda
                                        </label>
                                        <Input
                                            name="sale_price"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={productDraft.sale_price ?? ''}
                                            onChange={handleFormChange}
                                            placeholder="0,00"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            Usado como valor sugerido nos itens de pedido (pode ser sobrescrito por item).
                                        </p>
                                    </div>

                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Depósito padrão
                                        </label>
                                        <select
                                            name="default_warehouse_id"
                                            value={
                                                productDraft.default_warehouse_id != null
                                                    ? String(productDraft.default_warehouse_id)
                                                    : ''
                                            }
                                            onChange={handleFormChange}
                                            className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            <option value="">Nenhum</option>
                                            {warehouses.map((warehouse) => (
                                                <option key={warehouse.id} value={warehouse.id}>
                                                    {warehouse.name}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="mt-1 text-xs text-gray-500">
                                            Sugerido automaticamente nas movimentações de estoque deste produto.
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab: BOM - apenas se estiver editando */}
                    {isEditing && (
                        <TabsContent value="bom" activeValue={activeTab} className="mt-6">
                            {/* Cards de Estatísticas */}
                            {(() => {
                                const bomSupplies = productDraft.supplies || [];
                                const bomResources = productDraft.resources || [];

                                const totalSupplyCost = bomSupplies.reduce((sum, item) => {
                                    const qty = parseFloat(item.quantity_per_unit || 0);
                                    const unitCost = parseFloat(item.supply?.unit_cost || 0);
                                    return sum + (qty * unitCost);
                                }, 0);

                                const totalResourceCost = bomResources.reduce((sum, item) => {
                                    const qty = parseFloat(item.quantity_per_unit || 0);
                                    const unitCost = parseFloat(item.resource?.unit_cost || 0);
                                    return sum + (qty * unitCost);
                                }, 0);

                                const totalBOMCost = totalSupplyCost + totalResourceCost;

                                const formatCurrency = (value) => {
                                    return new Intl.NumberFormat('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                    }).format(value || 0);
                                };

                                return (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                        {/* Card Custo de Insumos */}
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Custo de Insumos</p>
                                                    <p className="text-2xl font-bold mt-1 text-gray-900">{formatCurrency(totalSupplyCost)}</p>
                                                </div>
                                                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card Custo de Recursos */}
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Custo de Recursos</p>
                                                    <p className="text-2xl font-bold mt-1 text-gray-900">{formatCurrency(totalResourceCost)}</p>
                                                </div>
                                                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card Custo Unitário do BOM */}
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Custo Unitário do BOM</p>
                                                    <p className="text-2xl font-bold mt-1 text-gray-900">{formatCurrency(totalBOMCost)}</p>
                                                </div>
                                                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {productDraft ? (
                                <Card>
                                    <CardContent className="pt-6">
                                        <BOMEditor
                                            ref={bomEditorRef}
                                            productId={id}
                                            bomData={{ supplies: productDraft.supplies || [], resources: productDraft.resources || [] }}
                                            onChange={(nextBom) => {
                                                setProductDraft((prev) => ({
                                                    ...prev,
                                                    supplies: nextBom.supplies || [],
                                                    resources: nextBom.resources || [],
                                                }));
                                            }}
                                            onSave={(payload) => {
                                                // Save is driven by product submit; keep draft in sync if user clicks editor save elsewhere.
                                                setProductDraft((prev) => ({
                                                    ...prev,
                                                    supplies: payload.supplies || [],
                                                    resources: payload.resources || [],
                                                }));
                                            }}
                                            hideSaveButton={true}
                                            supplies={supplies}
                                            resources={resources}
                                        />
                                    </CardContent>
                                </Card>
                            ) : null}
                        </TabsContent>
                    )}

                    {/* Tab: Movimentação de Estoque - apenas se estiver editando */}
                    {isEditing && (
                        <TabsContent value="stock" activeValue={activeTab} className="mt-6">
                            {/* Cards de Estatísticas */}
                            {(() => {
                                // Calcula saldo atual baseado nas movimentações
                                const currentBalance = stockMovements.reduce((sum, movement) => {
                                    const qty = parseFloat(movement.quantity) || 0;
                                    return sum + qty;
                                }, 0);

                                // Valores do produto ou valores padrão mock
                                const minStock = (() => {
                                    const a = productDraft?.min_stock;
                                    const b = product?.min_stock;
                                    if (a !== null && a !== undefined && a !== '' && !Number.isNaN(Number(a))) {
                                        return Number(a);
                                    }
                                    return b != null ? Number(b) : 0;
                                })();
                                const maxStock = (() => {
                                    const a = productDraft?.max_stock;
                                    const b = product?.max_stock;
                                    if (a !== null && a !== undefined && a !== '' && !Number.isNaN(Number(a))) {
                                        return Number(a);
                                    }
                                    return b != null ? Number(b) : 0;
                                })();

                                return (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                        {/* Card Mínimo */}
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Mínimo</p>
                                                    <p className="text-2xl font-bold mt-1 text-gray-900">{formatQuantity(minStock)}</p>
                                                </div>
                                                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card Máximo */}
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Máximo</p>
                                                    <p className="text-2xl font-bold mt-1 text-gray-900">{formatQuantity(maxStock)}</p>
                                                </div>
                                                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card Saldo Atual */}
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-600">Saldo Atual</p>
                                                    <p className={`text-2xl font-bold mt-1 ${currentBalance < minStock ? 'text-red-600' : currentBalance > maxStock ? 'text-yellow-600' : 'text-gray-900'}`}>
                                                        {formatQuantity(currentBalance)}
                                                    </p>
                                                </div>
                                                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle>Movimentação de Estoque</CardTitle>
                                        <Button type="button" onClick={openStockAdjustmentDrawer}>
                                            Ajuste de Inventário
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {stockMovementsLoading && stockMovements.length === 0 ? null : (
                                        <TableComponent
                                            columns={[
                                                {
                                                    key: 'date',
                                                    label: 'Data',
                                                    render: (value) => formatDateBR(value),
                                                },
                                                {
                                                    key: 'warehouse',
                                                    label: 'Depósito',
                                                    className: 'whitespace-nowrap',
                                                    render: (value, row) => row.warehouse?.name || '-',
                                                },
                                                {
                                                    key: 'type',
                                                    label: 'Tipo',
                                                    render: (value, row) => getMovementTypeDisplay(row),
                                                },
                                                {
                                                    key: 'document',
                                                    label: 'Documento',
                                                    render: (value, row) => {
                                                        const v = row.document ?? value;
                                                        const s =
                                                            v != null && String(v).trim() !== '' ? String(v) : '';
                                                        if (!s) return '-';
                                                        return <span className="whitespace-pre-line">{s}</span>;
                                                    },
                                                },
                                                {
                                                    key: 'quantity',
                                                    label: 'Quantidade',
                                                    render: (value) => (
                                                        <span className="font-medium text-gray-900">
                                                            {formatSignedQuantity(value)}
                                                        </span>
                                                    ),
                                                },
                                                {
                                                    key: 'empenho',
                                                    label: 'Empenho',
                                                    render: (value) =>
                                                        value != null && String(value).trim() !== '' ? String(value) : '-',
                                                },
                                                {
                                                    key: 'notes',
                                                    label: 'Observações',
                                                    render: (value) =>
                                                        value != null && String(value).trim() !== '' ? (
                                                            <span className="line-clamp-2" title={value}>{value}</span>
                                                        ) : '-',
                                                },
                                                {
                                                    key: 'origin',
                                                    label: 'Origem',
                                                    render: (value, row) => {
                                                        if (row.origin) {
                                                            const originType = row.origin.type || '';
                                                            const originRef = row.origin.reference || '';
                                                            const originDesc = row.origin.description || '';

                                                            const isProductionOrder = originType === 'production_order' ||
                                                                originType === 'production-order' ||
                                                                originRef.toLowerCase().includes('ordem') ||
                                                                originDesc.toLowerCase().includes('ordem de produção') ||
                                                                originDesc.toLowerCase().includes('produção');

                                                            const productionOrderId = row.origin.id ||
                                                                row.origin.production_order_id ||
                                                                row.origin.productionOrderId ||
                                                                (originRef.match(/#?(\d+)/)?.[1]);

                                                            return (
                                                                <div className="flex flex-col">
                                                                    {isProductionOrder && productionOrderId ? (
                                                                        <Link
                                                                            to={`/production/orders/${productionOrderId}`}
                                                                            className="font-medium text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                                                                        >
                                                                            {originRef}
                                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                            </svg>
                                                                        </Link>
                                                                    ) : (
                                                                        <span className="font-medium text-sm">{originRef}</span>
                                                                    )}
                                                                    {originDesc && (
                                                                        <span className="text-xs text-gray-500">{originDesc}</span>
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                        return <span className="text-gray-400">-</span>;
                                                    },
                                                },
                                                {
                                                    key: 'user',
                                                    label: 'Usuário',
                                                    render: (value, row) => row.user?.name || '-',
                                                },
                                            ]}
                                            data={stockMovements}
                                            emptyMessage="Nenhuma movimentação de estoque registrada"
                                            pagination={false}
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}
                </Tabs>
            </div>

            {/* Drawer de Ajuste de Inventário */}
            <Drawer
                open={adjustmentDrawerOpen}
                onClose={() => {
                    setAdjustmentDrawerOpen(false);
                    setAdjustmentFormData(buildFreshAdjustmentForm());
                }}
                title="Movimentação de estoque"
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setAdjustmentDrawerOpen(false);
                                setAdjustmentFormData(buildFreshAdjustmentForm());
                            }}
                            disabled={adjustmentLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            form="product-adjustment-form"
                            disabled={adjustmentLoading}
                        >
                            {adjustmentLoading ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </div>
                }
            >
                <form id="product-adjustment-form" onSubmit={handleAdjustmentSubmit} className="space-y-4">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        <span className="font-medium text-gray-900">Item: </span>
                        {productDraft.code ? `${productDraft.code} - ${productDraft.description || ''}` : '-'}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                        <MaskedInput
                            mask="date"
                            name="movement_date"
                            value={adjustmentFormData.movement_date}
                            onChange={handleAdjustmentFormChange}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Depósito *
                        </label>
                        <select
                            name="warehouse_id"
                            value={adjustmentFormData.warehouse_id}
                            onChange={handleAdjustmentFormChange}
                            className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                        >
                            <option value="">Selecione...</option>
                            {warehouses.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id}>
                                    {warehouse.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tipo de movimento *
                        </label>
                        <select
                            name="movement_type_id"
                            value={adjustmentFormData.movement_type_id}
                            onChange={handleAdjustmentFormChange}
                            className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                        >
                            <option value="">Selecione...</option>
                            {movementTypesCatalog.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.code} - {t.name} ({t.direction === 'in' ? 'entrada' : 'saída'})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantidade *
                        </label>
                        <Input
                            type="number"
                            step="0.001"
                            min="0.001"
                            name="quantity"
                            value={adjustmentFormData.quantity}
                            onChange={handleAdjustmentFormChange}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Documento</label>
                        <Input
                            type="text"
                            name="document"
                            value={adjustmentFormData.document}
                            onChange={handleAdjustmentFormChange}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Empenho</label>
                        <Input
                            type="text"
                            name="empenho"
                            value={adjustmentFormData.empenho}
                            onChange={handleAdjustmentFormChange}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Observações
                        </label>
                        <textarea
                            name="notes"
                            value={adjustmentFormData.notes}
                            onChange={handleAdjustmentFormChange}
                            rows={4}
                            className="flex w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                    </div>
                </form>
            </Drawer>
        </AppLayout>
    );
}

export default ProductForm;

